import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme, Tabs, Avatar, Button } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useShow, useBand, useVenue, useBandMusicians, useBandGenres, useBandShowComments, useBandShowMedia } from '../../api/queries';
import { create_show_comment, upload_show_media, delete_show } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import AccentButton from '../../components/AccentButton/AccentButton';
import SpecModal from '../../components/SpecModal/SpecModal';
import { useHeader } from '../../components/Layout/MainLayout';
import SpecActionSheet, { ActionSheetItem } from '../../components/SpecActionSheet/SpecActionSheet';
import { PlayButtonLarge } from '../../components/PlayButton/PlayButtonLarge';
import { DateSubheader } from '../../components/DateSubheader';
import { CalendarDateIcon, LocationMarkerIcon, ChatIcon, CameraIcon, PlusCircleIcon, DefaultAvatar, BandGroupIcon } from '../../components/icons';
import { Tag, TagRow } from '../../components/Tag';
import { PhotoRibbon } from '../../components/PhotoGallery/PhotoRibbon';
import { PhotoGrid } from '../../components/PhotoGallery/PhotoGrid';
import { PhotoGalleryDrawer } from '../../components/PhotoGallery/PhotoGalleryDrawer';
import { AddPhotoDrawer } from '../../components/PhotoGallery/AddPhotoDrawer';
import type { PhotoMetadata } from '../../components/PhotoGallery/PhotoOverlayInfo';
import { CommentList, CommentDrawer, AddCommentDrawer } from '../../components/Comment';
import type { CommentProps } from '../../components/Comment/Comment';
import FloatingPrimaryButton from '../../components/FloatingPrimaryButton/FloatingPrimaryButton';
import SpecAlert from '../../components/SpecAlert/SpecAlert';
import { useShare } from '../../hooks/useShare';
import AddToCalendarSheet from '../../components/AddToCalendarSheet/AddToCalendarSheet';
import SpecDrawer from '../../components/SpecDrawer/SpecDrawer';
import { MusicianListing } from '../../components/DirectoryListings/MusicianListing/MusicianListing';
import MapboxMap from '../../components/MapboxMap/MapboxMap';
import '../Show.css';

const ShowDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();
  const { user } = useAuth();
  const { data: show, isLoading, error } = useShow(id);
  const { data: band } = useBand(show?.band_id);
  const { data: venue } = useVenue(show?.venue_id);
  const { data: bandMusicians } = useBandMusicians(show?.band_id);
  const { data: bandGenres } = useBandGenres(show?.band_id);
  const { data: bandShowComments, refetch: refetchComments } = useBandShowComments(show?.band_id);
  const { data: bandShowMedia, refetch: refetchMedia } = useBandShowMedia(show?.band_id);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const share = useShare(setToastMessage);

  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [deleteShowModalOpen, setDeleteShowModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('comments');
  const [photoDrawerOpen, setPhotoDrawerOpen] = useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [addCommentDrawerOpen, setAddCommentDrawerOpen] = useState(false);
  const [initialCommentText, setInitialCommentText] = useState('');
  const [addPhotoDrawerOpen, setAddPhotoDrawerOpen] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const [highlightedCommentIndex, setHighlightedCommentIndex] = useState<number | null>(null);
  const [highlightedPhotoIndex, setHighlightedPhotoIndex] = useState<number | null>(null);
  const [bandMembersDrawerOpen, setBandMembersDrawerOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const { setOnMoreClick, setRightExtra, isAuthenticated } = useHeader();
  const commentListRef = useRef<HTMLDivElement>(null);
  const photoGridRef = useRef<HTMLDivElement>(null);

  // Build current user from auth context
  const currentUser = useMemo(() => {
    if (!user) return undefined;
    return {
      name: user.displayName || user.email.split('@')[0],
      avatarUrl: undefined, // Could be extended to store user avatar
    };
  }, [user]);

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format timestamp for comments (e.g., "Jan 15 3:29 PM")
  const formatTimestamp = (timestamp: string | { seconds: number } | undefined) => {
    if (!timestamp) return '';

    // Handle Firestore Timestamp object
    if (typeof timestamp === 'object' && 'seconds' in timestamp) {
      return dayjs.unix(timestamp.seconds).format('MMM D h:mm A');
    }

    // Handle ISO string or other date string
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Extract display name from what might be an email (Bug 55: use part before @)
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Extract day of month from date string
  const getDayOfMonth = (dateString: string) => {
    const date = new Date(dateString);
    return date.getDate();
  };

  // Filter comments and media for this specific show, sorted by latest first
  const showComments: Omit<CommentProps, 'className'>[] = (bandShowComments || [])
    .filter(comment => comment.show_id === id)
    .sort((a, b) => {
      const timeA = a.timestamp || a.created_at || '';
      const timeB = b.timestamp || b.created_at || '';
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    })
    .map(comment => {
      const rawUserId = comment.user_id || comment.created_by_id;
      const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
      return {
        userName: formatUserName(comment.user_name),
        avatarUrl: comment.user_avatar_url,
        timestamp: formatTimestamp(comment.timestamp || comment.created_at),
        userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
        text: comment.comment,
        userProfileUrl: userId ? `/fans/${userId}` : undefined,
        onUserClick: userId ? () => navigate(`/fans/${userId}`) : undefined,
      };
    });

  const showPhotos: string[] = (bandShowMedia || [])
    .filter(media => media.show_id === id)
    .map(media => media.url);

  const showPhotoMetadata: PhotoMetadata[] = (bandShowMedia || [])
    .filter(media => media.show_id === id)
    .map(media => {
      const rawUserId = media.user_id || media.created_by_id;
      const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
      return {
        userName: formatUserName(media.user_name),
        userAvatarUrl: media.user_avatar_url,
        timestamp: formatTimestamp(media.timestamp || media.created_at),
        userTags: (media.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
        userProfileUrl: userId ? `/fans/${userId}` : undefined,
        eventInfo: show ? {
          artistName: show.band_name,
          artistAvatarUrl: show.band_photo_url,
          date: formatDate(show.date),
          venue: show.venue_name || '',
        } : undefined,
      };
    });

  // Transform band musicians
  const bandMembers = (bandMusicians || []).map(musician => ({
    name: `${musician.first_name} ${musician.last_name}`,
    avatarUrl: musician.photo_url,
    id: musician.id,
  }));

  // Extract genres
  const genres: string[] = bandGenres && bandGenres.length > 0
    ? bandGenres.map(g => g.genre_name)
    : band?.genres && band.genres.length > 0
      ? band.genres
      : band?.genre
        ? [band.genre]
        : [];

  // Use server data directly
  const allComments = showComments;
  const allPhotos = showPhotos;
  const allPhotoMeta = showPhotoMetadata;

  const triggerAddPhoto = useCallback(() => {
    photoFileInputRef.current?.click();
  }, []);

  const handlePhotoFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingPhotoFile(file);
    setPendingPhotoPreview(URL.createObjectURL(file));
    setAddPhotoDrawerOpen(true);
    e.target.value = '';
  }, []);

  const handleAddPhotoClose = useCallback(() => {
    setAddPhotoDrawerOpen(false);
    setPendingPhotoFile(null);
    if (pendingPhotoPreview) {
      URL.revokeObjectURL(pendingPhotoPreview);
    }
    setPendingPhotoPreview(null);
  }, [pendingPhotoPreview]);

  const openAddComment = useCallback((prefill = '') => {
    setInitialCommentText(prefill);
    setAddCommentDrawerOpen(true);
  }, []);

  const handleEmojiClick = useCallback((emoji: string) => {
    setCommentDrawerOpen(false);
    openAddComment(emoji);
  }, [openAddComment]);

  const handleCommentSubmit = useCallback(async (data: { text: string; userName?: string }) => {
    if (!show || !id) return;

    // Use provided userName (guest) or fall back to authenticated user's name
    const userName = data.userName || currentUser?.name;
    // Authenticated users get 'fan' tag, guests get no tags (will show 'noAccount')
    const userTags = currentUser ? ['fan'] : [];

    try {
      await create_show_comment(show.band_id, id, {
        band_id: show.band_id,
        band_name: show.band_name,
        show_id: id,
        venue_id: show.venue_id,
        venue_name: show.venue_name,
        comment: data.text,
        user_name: userName,
        user_tags: userTags,
      });

      // Refetch comments to get the new one from the server
      await refetchComments();

      setAddCommentDrawerOpen(false);
      setActiveTab('comments');
      setHighlightedCommentIndex(0);
      setToastMessage('Comment Added');
      setTimeout(() => {
        const firstComment = commentListRef.current?.querySelector('.spec-comment');
        firstComment?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedCommentIndex(null), 3000);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to add comment');
    }
  }, [show, id, refetchComments, currentUser]);

  const handlePhotoSubmit = useCallback(async (data: { file: File; previewUrl: string; userName?: string }) => {
    if (!show || !id) return;

    // Use provided userName (guest) or fall back to authenticated user's name
    const userName = data.userName || currentUser?.name;
    // Authenticated users get 'fan' tag, guests get no tags (will show 'noAccount')
    const userTags = currentUser ? ['fan'] : [];

    try {
      setToastMessage('Uploading photo...');

      await upload_show_media(show.band_id, id, data.file, {
        band_name: show.band_name,
        venue_id: show.venue_id,
        venue_name: show.venue_name,
        user_name: userName,
        user_tags: userTags,
      });

      // Refetch media to get the new one from the server
      await refetchMedia();

      setAddPhotoDrawerOpen(false);
      setPendingPhotoFile(null);
      setPendingPhotoPreview(null);
      setActiveTab('photos');
      setHighlightedPhotoIndex(0);
      setToastMessage('Photo Added');
      setTimeout(() => {
        const firstPhoto = photoGridRef.current?.querySelector('.photo-grid-item');
        firstPhoto?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedPhotoIndex(null), 3000);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to upload photo');
    }
  }, [show, id, refetchMedia, currentUser]);

  // Register the More button callback and Add to Calendar header button
  useEffect(() => {
    setOnMoreClick(() => setActionSheetOpen(true));
    setRightExtra(
      <AccentButton
        size="small"
        shape="round"
        onClick={() => setCalendarSheetOpen(true)}
      >
        Add to Calendar
      </AccentButton>
    );

    return () => {
      setOnMoreClick(undefined);
      setRightExtra(undefined);
    };
  }, [setOnMoreClick, setRightExtra]);

  // Simulate audio progress when playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 300);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
  };

  if (isLoading) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Loading show...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
        Error loading show: {error.message}
      </div>
    );
  }

  if (!show) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Show not found.
      </div>
    );
  }

  const formattedDate = formatDate(show.date);
  const dayOfMonth = getDayOfMonth(show.date);
  const hasSoundcloud = !!show.band_soundcloud_url;

  const showContext = {
    bandName: show.band_name,
    venueName: show.venue_name || '',
    date: formattedDate,
    imageUrl: show.band_photo_url,
  };

  const actionSheetItems: ActionSheetItem[] = [
    {
      key: 'share',
      label: 'Share',
      onClick: () => share({ title: `${show.band_name} at ${show.venue_name}`, text: show.description, entityType: 'show', entityId: show.id }),
    },
    {
      key: 'copy-link',
      label: 'Copy Link',
      onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); setToastMessage('Link Copied'); },
    },
    {
      key: 'add-to-calendar',
      label: 'Add to Calendar',
      onClick: () => setCalendarSheetOpen(true),
    },
    {
      key: 'qr-code',
      label: 'QR Code',
      onClick: () => navigate(`/show/${show.id}/qr`),
    },
    {
      key: 'edit-show',
      label: 'Edit Show',
      onClick: () => navigate('/add-show', { state: { editMode: true, showId: show.id } }),
    },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
      onClick: () => setDeleteShowModalOpen(true),
    },
  ];

  // Create ISO date string for calendar
  const createCalendarDate = (dateStr: string, timeStr: string) => {
    // Parse the date string (YYYY-MM-DD format)
    const [year, month, day] = dateStr.split('-').map(Number);

    // Parse time like "8:00 PM"
    let hours = 12; // default to noon
    let minutes = 0;
    const timeMatch = timeStr?.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (timeMatch) {
      hours = parseInt(timeMatch[1]);
      minutes = parseInt(timeMatch[2]);
      const meridiem = timeMatch[3]?.toUpperCase();
      if (meridiem === 'PM' && hours < 12) hours += 12;
      if (meridiem === 'AM' && hours === 12) hours = 0;
    }

    // Create date with local timezone
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Format as local ISO string (without timezone conversion)
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
  };

  const startDate = createCalendarDate(show.date, show.time);
  const endDate = (() => {
    // Parse startDate and add 1 hour
    const [datePart, timePart] = startDate.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, mins] = timePart.split(':').map(Number);
    const d = new Date(year, month - 1, day, hours + 1, mins, 0, 0);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  })();

  return (
    <div className="show-page">
      {/* Fixed Blurred Hero */}
      <div className="show-page__hero">
        <div
          className="show-page__hero-bg"
          style={
            show.band_photo_url
              ? { backgroundImage: `url(${show.band_photo_url})` }
              : { backgroundColor: '#404040' }
          }
        />
        <div className="show-page__hero-overlay" />
      </div>

      {/* Play button — fixed above header */}
      {hasSoundcloud && (
        <div className="show-page__play-button">
          <PlayButtonLarge
            size={52}
            isPlaying={isPlaying}
            progress={progress}
            onClick={handlePlayToggle}
          />
        </div>
      )}

      {/* Transparent spacer */}
      <div className="show-page__hero-spacer" />

      {/* Content body */}
      <div className="show-page__content-body">
        <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

        {/* Sticky Date Subheader */}
        <div className="show-page__date-section">
          <DateSubheader
            dateLabel={formattedDate}
            showNavArrows={false}
            dateClickable={false}
            timeLabel={show.time}
          />
        </div>

        {/* Show Info Section */}
        <div className="show-page__info-section">
          <div className="show-page__info-header" onClick={() => navigate(`/bands/${show.band_id}`)}>
            <div className="show-page__band-avatar">
              {show.band_photo_url ? (
                <img
                  className="show-page__band-avatar-img"
                  src={show.band_photo_url}
                  alt={show.band_name}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 8,
                    background: '#404040',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BandGroupIcon size={48} />
                </div>
              )}
              <CalendarDateIcon
                day={dayOfMonth}
                size={28}
                className="show-page__band-avatar-calendar"
              />
            </div>
            <div className="show-page__info-text">
              <h1 className="show-page__band-name">{show.band_name}</h1>
              {bandMembers.length > 0 && (
                <div
                  className="show-page__with show-page__with--clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBandMembersDrawerOpen(true);
                  }}
                >
                  <Avatar.Group size="small" max={{ count: 3 }}>
                    {bandMembers.map((member, index) => (
                      <Avatar
                        key={index}
                        src={member.avatarUrl}
                        size="small"
                        icon={!member.avatarUrl ? <DefaultAvatar size={16} /> : undefined}
                      />
                    ))}
                  </Avatar.Group>
                  <span className="show-page__with-text">
                    With {bandMembers[0]?.name}{bandMembers.length > 1 ? ' and more...' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="show-page__venue-row" onClick={() => navigate(`/venues/${show.venue_id}`)}>
            {venue?.photo_url ? (
              <img
                src={venue.photo_url}
                alt={show.venue_name || 'Venue'}
                className="show-page__venue-photo"
              />
            ) : (
              <div className="show-page__venue-icon-circle">
                <LocationMarkerIcon style={{ width: '32px', height: '32px' }} color="rgba(171, 171, 171, 0.8)" />
              </div>
            )}
            <div className="show-page__venue-info">
              <span className="show-page__venue-name">{show.venue_name || 'Unknown Venue'}</span>
              {show.address && (
                <span
                  className="show-page__venue-address show-page__venue-address--clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(show.address!)}`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  {show.address}
                </span>
              )}
            </div>
          </div>

          {show.description && (
            <div className="show-page__description-group">
              <p className="show-page__description">{show.description}</p>
              <p className="show-page__posted-by">
                <span className="show-page__posted-by-icon">
                  <PlusCircleIcon style={{ color: '#404040' }} />
                </span>
                Posted by {show.band_name}
              </p>
            </div>
          )}
        </div>

        {/* Genre Tags */}
        {genres.length > 0 && (
          <div className="show-page__tags-section">
            <TagRow mode="ribbon">
              {genres.map((genre) => (
                <Tag key={genre}>{genre}</Tag>
              ))}
            </TagRow>
          </div>
        )}

        {/* Recent Photos Label */}
        {allPhotos.length > 0 && (
          <>
            <h6 className="show-page__section-label">Past Photos</h6>

            {/* Photo Ribbon */}
            <div className="show-page__ribbon-section">
              <PhotoRibbon
                photos={allPhotos}
                maxPhotos={10}
                photoMetadata={allPhotoMeta}
                showOverlayUserInfo
                showOverlayEventInfo
              />
            </div>
          </>
        )}

        {/* Tabs: Comments / Photos */}
        <div className="show-page__tabs-section">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            centered
            items={[
              {
                key: 'comments',
                label: (
                  <span className="show-page__tab-label">
                    <ChatIcon size="s" tone="inherit" />
                    Comments
                  </span>
                ),
                children: (
                  <div className="show-page__tab-content" ref={commentListRef}>
                    <CommentList
                      comments={allComments}
                      maxComments={3}
                      onViewAll={() => setCommentDrawerOpen(true)}
                      onAddComment={() => openAddComment()}
                      onEmojiClick={handleEmojiClick}
                      highlightedIndex={highlightedCommentIndex}
                    />
                  </div>
                ),
              },
              {
                key: 'photos',
                label: (
                  <span className="show-page__tab-label">
                    <CameraIcon size="s" tone="inherit" />
                    Photos
                  </span>
                ),
                children: (
                  <div className="show-page__tab-content" ref={photoGridRef}>
                    <PhotoGrid
                      photos={allPhotos}
                      maxPhotos={9}
                      onViewAll={() => setPhotoDrawerOpen(true)}
                      onAddPhoto={triggerAddPhoto}
                      photoMetadata={allPhotoMeta}
                      showOverlayUserInfo
                      showOverlayEventInfo
                      highlightedIndex={highlightedPhotoIndex}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Map Section */}
        {(show.latitude && show.longitude) && (
          <div className="show-page__map-section">
            <div className="show-page__map-container">
              <MapboxMap
                center={[show.longitude, show.latitude]}
                zoom={16}
                markers={[{
                  id: 'venue',
                  longitude: show.longitude,
                  latitude: show.latitude,
                  label: show.venue_name,
                }]}
                className="show-page__mapbox"
                interactive={false}
              />
              <div className="show-page__map-gradient" />
            </div>
            <div className="show-page__map-button">
              <AccentButton
                size="middle"
                onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(show.address || `${show.latitude},${show.longitude}`)}`, '_blank')}
              >
                Get Directions
              </AccentButton>
            </div>
          </div>
        )}

        {/* About Section */}
        {band?.bio && (
          <div className="show-page__about-section">
            <h2 className="show-page__about-title">About Band</h2>
            <p className="show-page__about-text">{band.bio}</p>
          </div>
        )}
      </div>{/* end content-body */}

      {/* Floating Action Buttons */}
      <div className="show-page__fab-row">
        <FloatingPrimaryButton
          icon={<ChatIcon size="m" tone="black" />}
          className={activeTab === 'comments' ? 'show-page__fab-pill' : 'show-page__fab-circle'}
          style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
          onClick={() => openAddComment()}
        >
          {activeTab === 'comments' ? 'Add Comment' : null}
        </FloatingPrimaryButton>

        <FloatingPrimaryButton
          icon={<CameraIcon size="m" tone="black" />}
          className={activeTab === 'photos' ? 'show-page__fab-pill' : 'show-page__fab-circle'}
          style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
          onClick={triggerAddPhoto}
        >
          {activeTab === 'photos' ? 'Add Photo' : null}
        </FloatingPrimaryButton>
      </div>

      {/* Photo Gallery Drawer */}
      <PhotoGalleryDrawer
        open={photoDrawerOpen}
        onClose={() => setPhotoDrawerOpen(false)}
        photos={allPhotos}
        title="Show Photos"
        onAddPhoto={() => { setPhotoDrawerOpen(false); triggerAddPhoto(); }}
        fullscreen
        drawerProps={{ zIndex: 1100 }}
        photoMetadata={allPhotoMeta}
        showOverlayUserInfo
      />

      {/* Comment Drawer */}
      <CommentDrawer
        open={commentDrawerOpen}
        onClose={() => setCommentDrawerOpen(false)}
        comments={allComments}
        title="Show Comments"
        onAddComment={() => { setCommentDrawerOpen(false); openAddComment(); }}
        onEmojiClick={(emoji) => { setCommentDrawerOpen(false); openAddComment(emoji); }}
        fullscreen
        drawerProps={{ zIndex: 1100 }}
      />

      {/* Add Comment Drawer */}
      <AddCommentDrawer
        open={addCommentDrawerOpen}
        onClose={() => setAddCommentDrawerOpen(false)}
        onSubmit={handleCommentSubmit}
        showContext={showContext}
        isLoggedIn={isAuthenticated}
        currentUser={currentUser}
        initialText={initialCommentText}
      />

      {/* Add Photo Drawer */}
      <input
        ref={photoFileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoFileSelected}
        style={{ display: 'none' }}
      />
      <AddPhotoDrawer
        open={addPhotoDrawerOpen}
        onClose={handleAddPhotoClose}
        onSubmit={handlePhotoSubmit}
        showContext={showContext}
        isLoggedIn={isAuthenticated}
        currentUser={currentUser}
        selectedFile={pendingPhotoFile}
        previewUrl={pendingPhotoPreview}
      />

      {/* Action Sheet */}
      <SpecActionSheet
        open={actionSheetOpen}
        onClose={() => setActionSheetOpen(false)}
        items={actionSheetItems}
      />

      {/* Add to Calendar Sheet */}
      <AddToCalendarSheet
        open={calendarSheetOpen}
        onClose={() => setCalendarSheetOpen(false)}
        event={{
          title: `${show.band_name} at ${show.venue_name}`,
          description: show.description || '',
          location: show.address || '',
          startDate,
          endDate,
        }}
      />

      {/* Delete Show Confirmation Modal */}
      <SpecModal
        open={deleteShowModalOpen}
        onCancel={() => setDeleteShowModalOpen(false)}
        title="Delete Show"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="show-page__modal-body">
          Are you sure you want to delete this show? This cannot be undone.
        </p>
        <div className="show-page__modal-actions">
          <Button
            size="large"
            shape="round"
            onClick={() => setDeleteShowModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="large"
            shape="round"
            danger
            type="primary"
            loading={deleting}
            onClick={async () => {
              if (!id) return;
              setDeleting(true);
              try {
                await delete_show(id);
                // Refetch shows before navigating
                await queryClient.invalidateQueries({ queryKey: ['shows'] });
                if (show?.band_id) {
                  await queryClient.invalidateQueries({ queryKey: ['band', show.band_id, 'shows'] });
                }
                setDeleteShowModalOpen(false);
                navigate('/home');
              } catch (error: any) {
                setToastMessage(error.message || 'Failed to delete show');
                setDeleting(false);
              }
            }}
          >
            Delete
          </Button>
        </div>
      </SpecModal>

      {/* Band Members Drawer */}
      <SpecDrawer
        title={
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.3)', borderRadius: 2, margin: '0 auto 24px', flexShrink: 0 }} />
            Band Members
          </div>
        }
        open={bandMembersDrawerOpen}
        onClose={() => setBandMembersDrawerOpen(false)}
        size="default"
        closable={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          {bandMembers.length > 0 ? (
            bandMembers.map((member, index) => (
              <MusicianListing
                key={index}
                name={member.name}
                imageUrl={member.avatarUrl}
                onClick={() => navigate(`/musicians/${member.id}`)}
              />
            ))
          ) : (
            <div className="view-all-drawer__empty">No band members listed</div>
          )}
        </div>
      </SpecDrawer>
    </div>
  );
};

export default ShowDetail;
